import { useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import { deriveSpecies, type Shot } from "../api/types";
import { fmtRelative, fmtTime, SPECIES_LABEL } from "./format";

function BellIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}

interface Props {
  newShots: Shot[];
  onMarkSeen: () => void;
  onClickShot?: (shot: Shot) => void;
  hrefForShot?: (shot: Shot) => string;
}

export function Bell({ newShots, onMarkSeen, onClickShot, hrefForShot }: Props) {
  const [open, setOpen] = useState(false);
  const popRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const t = window.setTimeout(() => document.addEventListener("mousedown", handler), 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener("mousedown", handler);
    };
  }, [open]);

  const has = newShots.length > 0;
  return (
    <div style={{ position: "relative" }}>
      <button
        className={`bell ${has ? "has-new" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-label={has ? `${newShots.length} new images` : "Notifications"}
      >
        <BellIcon />
        {has && <span className="bell-count">{newShots.length}</span>}
      </button>
      {open && (
        <div className="notif-pop" ref={popRef}>
          <div className="notif-pop-hd">
            <b>
              {has
                ? `${newShots.length} new ${newShots.length === 1 ? "frame" : "frames"}`
                : "All caught up"}
            </b>
            {has && (
              <button
                style={{
                  background: "none",
                  border: 0,
                  color: "var(--accent)",
                  cursor: "pointer",
                  fontFamily: "var(--mono)",
                  fontSize: "0.7em",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
                onClick={onMarkSeen}
              >
                Mark all seen
              </button>
            )}
          </div>
          {has ? (
            <ul>
              {[...newShots]
                .sort((a, b) => +new Date(b.capturedAt) - +new Date(a.capturedAt))
                .map((s) => {
                const captured = new Date(s.capturedAt);
                const species = deriveSpecies(s.analysis);
                const href = hrefForShot?.(s);
                return (
                  <li key={s.imageId}>
                    <a
                      className="notif-link"
                      href={href ?? "#"}
                      onClick={(e) => {
                        // Plain click: handle in-app (close popover, let route
                        // change via the href). Modified clicks (cmd/ctrl/shift/
                        // middle) fall through to the browser so users can open
                        // the entry in a new tab.
                        if (e.defaultPrevented) return;
                        if (e.button !== 0) return;
                        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                        onClickShot?.(s);
                        setOpen(false);
                      }}
                    >
                      <div className="notif-thumb">
                        <img src={api.imageContentUrl(s.imageId)} alt="" loading="lazy" />
                      </div>
                      <div className="notif-meta">
                        <span className="cam">{s.cameraId}</span>
                        <span className="ts">
                          {fmtTime(captured)} · {fmtRelative(captured)}
                        </span>
                      </div>
                      <span className="spacer" />
                      {species && (
                        <span
                          className="eyebrow"
                          style={{ fontSize: "0.62em", color: "var(--accent)" }}
                        >
                          {SPECIES_LABEL[species]}
                        </span>
                      )}
                    </a>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div
              style={{
                padding: "32px 22px",
                textAlign: "center",
                color: "var(--fg-mute)",
                fontFamily: "var(--serif)",
                fontStyle: "italic",
              }}
            >
              No new frames since your last visit. The woods are quiet.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
